import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useStore } from '../../../../store';
import { isUserLoggedIn } from '../../../../utils/analytics';
import CollectionItem from '../../../CollectionItem';

const CollectionsDisplayWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.tablet}px) {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 14px;
  }
  @media screen and (min-width: ${({ theme }) => theme.breakpoints.headerBreakpoint}px) {
    gap: 22px;
  }
`;

type CollectionsDisplayProps = {
  creatorCollections: CollectionEdgeResponse[];
  sectionName: string;
};

const CollectionsDisplay: React.FC<CollectionsDisplayProps> = ({
  creatorCollections,
  sectionName,
}: CollectionsDisplayProps) => {
  const { state } = useStore();
  const [showProductsOverlayFor, setShowProductsOverlayFor] = React.useState('');
  const targetRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const threshold = 300;
        let closestElement: string | null = null;
        let minDistance = Infinity;

        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const targetId = entry.target.getAttribute('data-id');
            if (targetId) {
              const boundingTop = entry.boundingClientRect.top;
              const midpoint = boundingTop + entry.boundingClientRect.height / 2;
              const distance = Math.abs(midpoint - threshold);
              if (distance < minDistance) {
                minDistance = distance;
                closestElement = targetId;
              }
            }
          }
        });

        const newValue = closestElement || '';

        // Only update if the value actually changed
        setShowProductsOverlayFor((prev) => (prev !== newValue ? newValue : prev));
      },
      { threshold: 0.4, rootMargin: '100px 0px 0px 0px' },
    );

    // Observe each target element
    Object.values(targetRefs.current).forEach((target) => {
      if (target) {
        observer.observe(target);
      }
    });

    // Cleanup function
    return () => {
      Object.values(targetRefs.current).forEach((target) => {
        if (target) {
          observer.unobserve(target);
        }
      });
    };
  }, [showProductsOverlayFor]);

  return (
    <CollectionsDisplayWrapper>
      {creatorCollections.map((collection, index) => {
        const { node } = collection;
        return (
          <CollectionItem
            key={node.handle}
            node={node}
            index={index}
            sectionName={sectionName}
            showProducts={showProductsOverlayFor === node.handle}
            registerRef={(el) => {
              targetRefs.current[node.handle] = el;
            }}
            isUserLoggedIn={isUserLoggedIn(state.user)}
          />
        );
      })}
    </CollectionsDisplayWrapper>
  );
};

export default CollectionsDisplay;
